<template>
  <!-- Overview tab of a contract: in short, what it covers, the agreement and where the data goes (US 9371) -->
  <div v-if="summary" class="sp-overview" qid="contract-overview">
    <div class="sp-main">
      <!-- In short: the contract in a few sentences, built from what's filled in -->
      <section class="v2-card sp-sec cp-short" qid="contract-in-short">
        <div class="cp-short__body">
          <font-awesome-icon icon="sparkles" class="cp-short__icon"/>
          <div>
            <small class="cp-short__label">{{ $t('contract_page.short.title') }}</small>
            <p class="cp-short__text">
              <span v-for="(sentence, index) in sentences" :key="index" :class="{ 'is-warn': sentence.warn }">{{ sentence.text }} </span>
            </p>
          </div>
        </div>
      </section>

      <!-- What it covers: the systems that run on it or are reached through it, and their activities -->
      <section v-if="modules.systems" class="v2-card sp-sec" qid="contract-covers">
        <div class="sp-sec__head">
          <font-awesome-icon icon="desktop"/>
          <h3>{{ $t('contract_page.covers.title') }}</h3>
          <span class="sp-pips"><i :class="{ 'is-no': !summary.systems.length }"></i></span>
          <span class="sp-sec__state">{{ summary.systems.length ? $t('system_hero.all_filled') : $t('contract_page.covers.none_short') }}</span>
        </div>
        <template v-for="group in systemGroups">
          <div v-if="group.items.length" :key="group.key" class="sp-group" :class="'is-' + group.key">{{ $t('system_page.where.groups.' + group.key) }}</div>
          <div v-for="item in group.items" :key="group.key + item.link_id" class="sp-kv" :class="{ 'is-unsorted': group.key === 'unsorted' }">
            <div class="sp-kv__k">{{ item.covers ? $t('systems_overview.covers.' + item.covers) : $t('systems_overview.covers.none') }}</div>
            <div class="sp-kv__v">
              <div class="sp-contract">
                <div class="sp-contract__top">
                  <span class="cp-sys-logo" :class="{ 'has-image': item.logo }">
                    <img v-if="item.logo" :src="item.logo" alt="">
                    <font-awesome-icon v-else icon="desktop"/>
                  </span>
                  <router-link :to="{ name: 'systems.view', params: { slug: item.slug } }" class="sp-contract__name">{{ item.name }}</router-link>
                  <span v-if="item.runs_as" class="sp-muted">{{ $t('systems_overview.runs_as.' + item.runs_as) }}</span>
                  <span class="sp-grow"></span>
                  <span v-if="item.processing_role" class="sp-muted">{{ role(item.processing_role) }}</span>
                </div>
                <div v-if="item.activity_count" class="sp-contract__facts">
                  <span class="sp-chip">{{ $tc('contract_page.covers.activities', item.activity_count, { count: item.activity_count }) }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div v-if="!summary.systems.length" class="sp-kv is-missing">
          <div class="sp-kv__k"></div>
          <div class="sp-kv__v"><span class="sp-muted">{{ $t('contract_page.covers.none') }}</span></div>
        </div>
        <div v-if="modules.activities && summary.activities.total" class="sp-kv">
          <div class="sp-kv__k">{{ $t('contract_page.covers.activities_label') }}</div>
          <div class="sp-kv__v">
            <router-link v-for="activity in summary.activities.items.slice(0, 6)"
                         :key="activity.id"
                         :to="{ name: 'activities.view', params: { slug: activity.slug } }"
                         class="sp-chip">{{ activity.name | truncate(40) }}</router-link>
            <span v-if="summary.activities.total > 6" class="sp-muted">+{{ summary.activities.total - 6 }}</span>
          </div>
        </div>
        <div class="sp-sec__foot">
          <button type="button" class="v2-link" qid="contract-open-where-used" @click="$emit('open-tab', 'where-used')">
            <font-awesome-icon icon="link"/>{{ $t('contract_page.covers.manage') }}
          </button>
        </div>
      </section>

      <!-- The agreement -->
      <section class="v2-card sp-sec" qid="contract-agreement">
        <div class="sp-sec__head">
          <font-awesome-icon icon="file-signature"/>
          <h3>{{ $t('contract_page.agreement.title') }}</h3>
          <span class="sp-grow"></span>
          <button v-if="canEdit" type="button" class="v2-link" qid="contract-agreement-edit" @click="$emit('edit')">
            <font-awesome-icon icon="pen"/>{{ $t('system_hero.edit') }}
          </button>
        </div>
        <div class="sp-kv">
          <div class="sp-kv__k">{{ $t('contract_page.agreement.third_party') }}</div>
          <div class="sp-kv__v">
            <router-link v-for="vendor in vendors" :key="vendor.id" :to="{ name: 'contracts.third_parties.view', params: { slug: vendor.slug } }" class="sp-chip">
              <font-awesome-icon icon="building"/> {{ vendor.name }}
            </router-link>
            <span v-if="!vendors.length" class="sp-muted">{{ $t('contract_page.agreement.no_third_party') }}</span>
          </div>
        </div>
        <div class="sp-kv">
          <div class="sp-kv__k">{{ $t('contract_page.agreement.role') }}</div>
          <div class="sp-kv__v">
            <span v-for="item in roles" :key="item" class="sp-chip">{{ role(item) }}</span>
            <span v-if="!roles.length" class="sp-muted">{{ $t('contract_page.agreement.no_role') }}</span>
          </div>
        </div>
        <div v-if="modules.gdpr" class="sp-kv" :class="{ 'is-missing': !contract.data_processor_agreement }">
          <div class="sp-kv__k">{{ $t('contract_page.agreement.dpa') }}</div>
          <div class="sp-kv__v">
            <span v-if="contract.data_processor_agreement" class="sp-badge" :class="contract.data_processor_agreement === 'Yes' ? 'is-ok' : (contract.data_processor_agreement === 'No' ? 'is-warn' : '')">
              {{ value('data_processor_agreement', contract.data_processor_agreement) }}
            </span>
            <span v-else class="sp-muted">{{ $t('contract_page.agreement.not_answered') }}</span>
            <span v-if="contract.dpa_updated_at" class="sp-muted">{{ $t('contract_page.agreement.updated', { date: date(contract.dpa_updated_at) }) }}</span>
          </div>
        </div>
        <div v-if="contract.inspection" class="sp-kv">
          <div class="sp-kv__k">{{ $t('maintenance.third_parties.table.inspection') }}</div>
          <div class="sp-kv__v">
            <span class="sp-chip">{{ value('inspection', contract.inspection) }}</span>
            <span v-if="contract.inspection_updated_at" class="sp-muted">{{ date(contract.inspection_updated_at) }}</span>
          </div>
        </div>
        <div v-if="contract.reference" class="sp-kv">
          <div class="sp-kv__k">{{ $t('maintenance.third_parties.create.contract_reference_label') }}</div>
          <div class="sp-kv__v">{{ contract.reference }}</div>
        </div>
        <div v-if="modules.documents" class="sp-kv">
          <div class="sp-kv__k">{{ $t('contract_page.agreement.documents') }}</div>
          <div class="sp-kv__v cp-docs">
            <div v-for="document in summary.documents" :key="document.id" class="cp-doc">
              <font-awesome-icon icon="file-alt"/>
              <span class="cp-doc__name">{{ document.name | truncate(50) }}</span>
              <span class="sp-muted">{{ document.end_date ? $t('contract_page.agreement.ends', { date: date(document.end_date) }) : $t('contract_page.agreement.no_end') }}</span>
              <span v-if="ending(document)" class="sp-badge is-warn">{{ $t('contract_page.agreement.ends_soon') }}</span>
            </div>
            <span v-if="!summary.documents.length" class="sp-muted">{{ $t('contract_page.agreement.no_documents') }}</span>
            <button type="button" class="v2-link sp-sub" @click="$emit('open-tab', 'documents')">{{ $t('contract_page.agreement.open_documents') }}</button>
          </div>
        </div>
      </section>

      <!-- Where the data goes -->
      <section v-if="modules.gdpr" class="v2-card sp-sec" qid="contract-data-goes">
        <div class="sp-sec__head">
          <font-awesome-icon icon="globe"/>
          <h3>{{ $t('contract_page.data.title') }}</h3>
          <span class="sp-pips">
            <i :class="{ 'is-no': !summary.locations.length }"></i>
            <i :class="{ 'is-no': !contract.transfers_disclosures_third_countries }"></i>
          </span>
          <span class="sp-sec__state">{{ dataState }}</span>
        </div>
        <div class="sp-kv" :class="{ 'is-missing': !summary.locations.length }">
          <div class="sp-kv__k">{{ $t('maintenance.third_parties.create.country_label') }}</div>
          <div class="sp-kv__v">
            <span v-for="country in summary.locations" :key="country.name" class="sp-chip" :class="{ 'is-sens': country.eea === false }">
              <img v-if="country.flag" :src="country.flag" height="11" class="cp-flag" alt="">{{ country.name }}
            </span>
            <span v-if="!summary.locations.length" class="sp-muted">{{ $t('contract_page.data.no_locations') }}</span>
          </div>
        </div>
        <!-- Each country outside the EU/EEA with its own transfer basis (US 9371) -->
        <div class="sp-kv" :class="{ 'is-missing': missingBases.length > 0 }">
          <div class="sp-kv__k">{{ $t('contract_page.data.outside') }}</div>
          <div class="sp-kv__v cp-bases">
            <div v-for="country in summary.outside_eea" :key="country.name" class="cp-basis" :class="{ 'is-warn': !country.valid }" :qid="'transfer-basis-' + country.name">
              <span class="cp-basis__country"><img v-if="country.flag" :src="country.flag" height="11" class="cp-flag" alt="">{{ country.name }}</span>
              <b-form-select v-if="canEdit"
                             size="sm"
                             class="cp-basis__select"
                             :value="country.valid ? country.basis : null"
                             :options="basisOptions"
                             :disabled="savingBasis === country.name"
                             @change="setBasis(country, $event)"></b-form-select>
              <span v-else class="sp-chip">{{ basisLabel(country.basis) }}</span>
              <span v-if="country.basis === 'privacy_shield'" class="sp-badge is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('contract_page.data.privacy_shield') }}</span>
              <span v-else-if="!country.basis" class="sp-badge is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('vendor_transfer.none') }}</span>
              <small v-else-if="country.legacy" class="sp-muted">{{ $t('vendor_transfer.from_old_field') }}</small>
            </div>
            <span v-if="!summary.outside_eea.length" class="sp-muted">{{ $t('contract_page.data.none_outside') }}</span>
          </div>
        </div>
        <div v-if="modules.subProcessors" class="sp-kv">
          <div class="sp-kv__k">{{ $t('maintenance.third_parties.tab.sub_data_processors') }}</div>
          <div class="sp-kv__v cp-subs">
            <div v-for="item in summary.sub_processors.slice(0, 6)" :key="item.id" class="cp-sub">
              <font-awesome-icon icon="building"/>
              <span class="cp-sub__name">{{ (item.vendor || item.name) | truncate(40) }}</span>
              <span class="sp-muted">{{ item.countries.map(country => country.name).join(', ') }}</span>
              <span v-if="item.countries.some(country => country.eea === false)" class="sp-badge is-warn">{{ $t('contract_page.data.outside_short') }}</span>
            </div>
            <span v-if="summary.sub_processors.length > 6" class="sp-muted">+{{ summary.sub_processors.length - 6 }}</span>
            <span v-if="!summary.sub_processors.length" class="sp-muted">{{ $t('contract_page.data.no_sub_processors') }}</span>
            <button type="button" class="v2-link sp-sub" @click="$emit('open-tab', 'data-privacy')">{{ $t('contract_page.data.open') }}</button>
          </div>
        </div>
      </section>
    </div>

    <div class="sp-side">
      <!-- Where it's used -->
      <section class="v2-card sp-side__card" qid="contract-where-used">
        <h4><font-awesome-icon icon="link"/>{{ $t('system_page.used.title') }}</h4>
        <ul class="sp-used">
          <li v-for="item in usedItems" :key="item.key" :class="'is-' + item.module">
            <b>{{ item.count }}</b>{{ $tc('contract_page.used.' + item.key, item.count) }}
          </li>
        </ul>
        <button type="button" class="v2-link sp-open" @click="$emit('open-tab', 'where-used')">
          {{ $t('system_where_used.open') }} <font-awesome-icon icon="arrow-right"/>
        </button>
      </section>
    </div>
  </div>

  <!-- While the overview loads: the outline of the two columns, so the page doesn't jump -->
  <div v-else class="sp-overview" aria-hidden="true">
    <div class="sp-main">
      <div class="v2-card sp-sec cp-loading"><span class="sx-skel is-long"></span><span class="sx-skel is-short"></span></div>
      <div class="v2-card sp-sec cp-loading"><span class="sx-skel is-title"></span><span class="sx-skel is-long"></span></div>
    </div>
    <div class="sp-side">
      <div class="v2-card sp-side__card cp-loading"><span class="sx-skel is-short"></span><span class="sx-skel is-long"></span></div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import {endedOn} from '@/util/contractEnd.js';
  import piincHttp from '@/util/configureAxios.js';
  import {BASIS_CHOICES, basisLabel} from '@/util/transferBasis.js';

  export default {
    name: 'ContractOverviewTab',
    props: {
      contract: { type: Object, required: true }
    },
    data() {
      return {
        summary: null,
        savingBasis: ''
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      modules() {
        let client = this.getClient;
        return {
          gdpr: !!client.gdprModule,
          subProcessors: !!client.gdprAdvancedModule,
          systems: !!client.systemModule,
          activities: !!client.processingActivitiesModule,
          documents: !!client.contractAdvancedModule,
          risk: !!client.riskModule
        };
      },
      canEdit() {
        return this.isGranted([this.permissions.THIRD_PARTY_EDIT, this.permissions.THIRD_PARTY_EDIT_OWN], this.contract)
          && (!this.getClient.parent || this.contract.partition);
      },
      vendors() {
        return this.contract.vendors || [];
      },
      roles() {
        let roles = this.contract.processing_roles || [];
        // Roles set on the links to systems, so they show even before the contract itself is reloaded
        (this.summary ? this.summary.systems : []).forEach(item => {
          if (item.processing_role && !roles.includes(item.processing_role)) {
            roles = roles.concat([item.processing_role]);
          }
        });
        return roles;
      },
      // Runs on, Have access, and links that aren't sorted yet (as on the system page, US 9370)
      systemGroups() {
        let systems = this.summary.systems;
        let runsOn = ['software_hosting', 'hosting', 'licence'];
        return [
          { key: 'runs_on', items: systems.filter(item => runsOn.includes(item.covers)) },
          { key: 'access', items: systems.filter(item => item.covers && !runsOn.includes(item.covers)) },
          { key: 'unsorted', items: systems.filter(item => !item.covers) }
        ];
      },
      // Countries outside the EU/EEA without a valid basis (none, or only the EU/US Privacy Shield)
      missingBases() {
        return this.summary.outside_eea.filter(country => !country.valid);
      },
      basisOptions() {
        return [{ value: null, text: this.$t('vendor_transfer.choose'), disabled: true }]
          .concat(BASIS_CHOICES.map(code => ({ value: code, text: this.basisLabel(code) })));
      },
      dataState() {
        let missing = (this.summary.locations.length ? 0 : 1) + this.missingBases.length;
        return missing ? this.$tc('contract_page.missing', missing, { count: missing }) : this.$t('system_hero.all_filled');
      },
      usedItems() {
        let counts = this.summary.counts;
        let modules = this.modules;
        return [
          { key: 'systems', count: counts.systems, module: 'gdpr', show: modules.systems },
          { key: 'activities', count: counts.activities, module: 'gdpr', show: modules.activities },
          { key: 'sub_processors', count: counts.sub_processors, module: 'gdpr', show: modules.subProcessors },
          { key: 'data_categories', count: counts.data_categories, module: 'gdpr', show: modules.gdpr },
          { key: 'risks', count: counts.risks, module: 'grc', show: modules.risk },
          { key: 'documents', count: counts.documents, module: 'gdpr', show: modules.documents }
        ].filter(item => item.show);
      },
      vendorName() {
        return this.vendors.length ? this.vendors.map(vendor => vendor.name).join(', ') : this.$t('contract_page.short.the_third_party');
      },
      // The contract in plain sentences, only from what's filled in
      sentences() {
        let list = [];
        let counts = this.summary.counts;
        let roles = this.roles.map(item => this.role(item).toLowerCase());
        let systems = this.$tc('contract_page.short.systems', counts.systems, { count: counts.systems });
        let activities = this.$tc('contract_page.short.activities', counts.activities, { count: counts.activities });
        if (this.contract.ended_at) {
          list.push({ text: this.$t('contract_end.short', { date: endedOn(this.contract.ended_at) }), warn: true });
        }
        if (roles.length) {
          list.push({ text: this.$t('contract_page.short.role', { vendor: this.vendorName, role: roles.join(' / '), systems: systems, activities: activities }) });
        } else {
          list.push({ text: this.$t('contract_page.short.no_role', { vendor: this.vendorName, systems: systems, activities: activities }) });
        }
        if (this.modules.gdpr) {
          let dpa = this.contract.data_processor_agreement;
          let key = dpa === 'Yes' ? 'dpa_yes' : (dpa === 'No' ? 'dpa_no' : (dpa ? 'dpa_na' : 'dpa_missing'));
          list.push({ text: this.$t('contract_page.short.' + key), warn: dpa === 'No' && roles.length > 0 });
          let locations = this.summary.locations.map(country => country.name);
          list.push(locations.length
            ? { text: this.$t('contract_page.short.locations', { list: locations.join(', ') }) }
            : { text: this.$t('contract_page.short.no_locations'), warn: true });
          let outside = this.summary.outside_eea.map(country => country.name);
          if (outside.length) {
            let missing = this.missingBases.map(country => country.name);
            list.push(missing.length
              ? { text: this.$t('contract_page.short.outside_no_basis', { list: missing.join(', ') }), warn: true }
              : { text: this.$t('vendor_transfer.short_all', { list: outside.join(', ') }) });
          }
        }
        return list;
      }
    },
    watch: {
      'contract.id': function () {
        this.load();
      },
      // The contract was edited: its counts and facts may have changed
      'contract.updated_at': function () {
        this.load();
      }
    },
    methods: {
      load() {
        let self = this;
        piincHttp.get('third-parties/' + this.contract.slug + '/overview', { params: { client: this.getClient.slug } }).then(function(response) {
          self.summary = response.data;
          self.$emit('summary', response.data);
        }, function() {});
      },
      role(value) {
        let key = 'maintenance.third_parties.processing_role.' + this.$options.filters.slugify(value);
        return this.$te(key) ? this.$t(key) : value;
      },
      // A stored answer (Yes, No, N/A, a contract type…) in the user's language
      value(group, value) {
        let key = 'maintenance.third_parties.' + group + '.' + this.$options.filters.slugify(value);
        return this.$te(key) ? this.$t(key) : value;
      },
      basisLabel(code) {
        return basisLabel(this, code);
      },
      // Saved straight away and logged on the contract
      setBasis(country, basis) {
        let self = this;
        this.savingBasis = country.name;
        piincHttp.put('third-parties/' + this.contract.slug + '/transfer-basis', { country: country.name, basis: basis }).then(function() {
          self.savingBasis = '';
          self.$toastr('success', self.$t('vendor_transfer.saved', { country: country.name }));
          self.load();
        }, function() {
          self.savingBasis = '';
        });
      },
      // Dates come as midnight UTC; read only the day, so it doesn't move a day back west of UTC
      date(value) {
        return moment(String(value).slice(0, 10), 'YYYY-MM-DD').format('D MMM YYYY');
      },
      // Ends within 90 days
      ending(document) {
        return document.end_date && moment(document.end_date).isBefore(moment().add(90, 'days')) && moment(document.end_date).isAfter(moment().subtract(1, 'day'));
      }
    },
    created() {
      if (this.contract.id) {
        this.load();
      }
    }
  }
</script>
